/**
 * Wrappers type-safe pour les fonctions RPC Supabase
 *
 * Centralise tous les appels RPC avec :
 * - Types de retour explicites
 * - Validation des paramètres
 * - Gestion d'erreurs cohérente
 * - Conversions de types gérées
 */

import { supabase } from '@/lib/supabase'
import { DatabaseExtensions, typedRpc } from '../types/supabase-extensions'

/**
 * Documents & Folders
 */
export const documentRpc = {
  /**
   * Supprime un dossier de manière intelligente
   * - Physical delete si vide
   * - Soft delete si contient des documents
   */
  async deleteFolderSmart(folderId: string) {
    const { data, error } = await supabase.rpc('delete_folder_smart', {
      p_folder_id: folderId,
    })

    if (error) throw error
    return typedRpc<DatabaseExtensions.DeleteFolderSmartResult>(data)
  },

  /**
   * Déplace un document vers un autre dossier
   * Gère automatiquement le réordonnancement
   */
  async moveDocumentToFolder(documentId: string, folderId: string | null) {
    const { data, error } = await supabase.rpc('move_document_to_folder', {
      p_document_id: documentId,
      // Conversion: null → string vide pour RPC qui attend string
      p_new_folder_id: folderId ?? '',
    })

    if (error) throw error
    return typedRpc<DatabaseExtensions.MoveDocumentToFolderResult>(data)
  },

  /**
   * Réordonne les documents dans un dossier
   */
  async reorderDocuments(documentIds: string[], folderId: string) {
    const { data, error } = await supabase.rpc('reorder_documents', {
      p_document_ids: documentIds,
      p_folder_id: folderId,
    })

    if (error) throw error
    return data
  },

  /**
   * Supprime un document et réordonne les autres
   */
  async deleteDocumentAndReorder(documentId: string) {
    const { data, error } = await supabase.rpc('delete_document_and_reorder', {
      p_document_id: documentId,
    })

    if (error) throw error
    return data
  },

  /**
   * Récupère le chemin complet d'un dossier
   */
  async getFolderPath(folderId: string) {
    const { data, error } = await supabase.rpc('get_folder_path', {
      folder_id: folderId,
    })

    if (error) throw error
    return data as string
  },

  /**
   * Crée les dossiers par défaut pour une résidence
   */
  async createDefaultFoldersForResidence(residenceId: string) {
    const { error } = await supabase.rpc('create_default_folders_for_residence', {
      p_residence_id: residenceId,
    })

    if (error) throw error
  },

  /**
   * Récupère les dossiers d'un copropriétaire pour une résidence
   */
  async getOwnerFoldersForResidence(residenceId: string, ownerId: string) {
    const { data, error } = await supabase.rpc('get_owner_folders_for_residence', {
      p_residence_id: residenceId,
      p_owner_id: ownerId,
    })

    if (error) throw error
    return typedRpc<DatabaseExtensions.OwnerFolderResult[]>(data)
  },
} as const

/**
 * Owners & Units
 */
export const ownerRpc = {
  /**
   * Vérifie les propriétaires actuels d'un lot
   */
  async checkUnitOwnerships(unitId: string) {
    const { data, error } = await supabase.rpc('check_unit_ownerships', {
      p_unit_id: unitId,
    })

    if (error) throw error
    return typedRpc<DatabaseExtensions.CheckUnitOwnershipsResult[]>(data)
  },

  /**
   * Crée un copropriétaire avec distribution automatique des parts
   */
  async createOwnerWithDistribution(params: {
    firstName: string
    lastName: string
    email?: string
    phone?: string
    address?: string
    preferredContact?: string
    unitDistributions?: unknown
    existingUnits?: unknown
    newUnits?: unknown
  }) {
    const { data, error } = await supabase.rpc('create_owner_with_distribution', {
      p_first_name: params.firstName,
      p_last_name: params.lastName,
      p_email: params.email,
      p_phone: params.phone,
      p_address: params.address,
      p_preferred_contact: params.preferredContact,
      p_unit_distributions: (params.unitDistributions ?? undefined) as any,
      p_existing_units: (params.existingUnits ?? undefined) as any,
      p_new_units: (params.newUnits ?? undefined) as any,
    })

    if (error) throw error
    return typedRpc<DatabaseExtensions.CreateOwnerWithDistributionResult>(data)
  },

  /**
   * Crée un dossier physique pour un copropriétaire
   */
  async createOwnerPhysicalFolder(agencyId: string, ownerId: string) {
    const { data, error } = await supabase.rpc('create_owner_physical_folder', {
      p_agency_id: agencyId,
      p_owner_id: ownerId,
    })

    if (error) throw error
    return data as string // Returns folder_id
  },
} as const

/**
 * Audit & History
 */
export const auditRpc = {
  /**
   * Récupère l'historique d'audit d'un enregistrement
   */
  async getRecordHistory(tableName: string, recordId: string) {
    const { data, error } = await supabase.rpc('get_record_history', {
      p_table_name: tableName,
      p_record_id: recordId,
    })

    if (error) throw error
    return typedRpc<DatabaseExtensions.AuditLogEntry[]>(data)
  },
} as const

/**
 * Residences
 */
export const residenceRpc = {
  /**
   * Récupère le gestionnaire principal d'une résidence
   */
  async getResidenceManager(residenceId: string) {
    const { data, error } = await supabase.rpc('get_residence_manager', {
      p_residence_id: residenceId,
    })

    if (error) throw error

    // La fonction retourne un tableau, prendre le premier
    const managers = typedRpc<DatabaseExtensions.ResidenceManagerResult[]>(data)
    return managers.length > 0 ? managers[0] : null
  },
} as const

/**
 * Subscription & Billing
 */
export const subscriptionRpc = {
  /**
   * Récupère les limites et l'usage de l'abonnement
   */
  async getSubscriptionLimits(agencyId: string) {
    const { data, error } = await supabase.rpc('get_subscription_limits', {
      p_agency_id: agencyId,
    })

    if (error) throw error
    return typedRpc<DatabaseExtensions.SubscriptionLimitsResult>(data)
  },
} as const

/**
 * Pricing
 */
export const pricingRpc = {
  /**
   * Récupère les données de tarification depuis la vue
   */
  async getPricingTiers() {
    const { data, error } = await supabase
      .from('pricing_table_data')
      .select('*')
      .order('price_monthly', { ascending: true })

    if (error) throw error
    return typedRpc<DatabaseExtensions.PricingTierData[]>(data)
  },
} as const

/**
 * Co-Owner Folders
 */
export const coOwnerRpc = {
  /**
   * Récupère les dossiers copropriétaires pour une résidence
   */
  async getCoOwnerFolders(residenceId: string, showInactive = false) {
    const { data, error } = await supabase.rpc(
      'get_owner_folders_for_residence',
      {
        p_residence_id: residenceId,
        p_show_inactive: showInactive,
      }
    )

    if (error) throw error
    return typedRpc<DatabaseExtensions.CoOwnerFolderLink[]>(data)
  },
} as const

/**
 * Export consolidé de tous les RPC
 */
export const typedSupabaseRpc = {
  documents: documentRpc,
  owners: ownerRpc,
  audit: auditRpc,
  residences: residenceRpc,
  subscription: subscriptionRpc,
  pricing: pricingRpc,
  coOwners: coOwnerRpc,
} as const
